/** subject -> topModuleId -> itemId[] */
export type NestedIds = Record<string, Record<string, string[]>>;

/**
 * Reserved system tags used by dedicated UI affordances (e.g. the thumbs-up/down
 * buttons on questions). They live in the normal tag store so they get the same
 * per-user persistence for free, but they are filtered out of the regular tag
 * picker/display so users don't see them as ordinary tags.
 *
 * **Mirrored from `@padcanvas/shared/userTagConstants`** — keep these values in
 * sync. shared-ui cannot import from `@padcanvas/shared` directly. The backend
 * is the source of truth and enforces creation-rejection + mutual exclusion.
 */
export const RESERVED_TAG_THUMBS_UP = '__thumbsup';
export const RESERVED_TAG_THUMBS_DOWN = '__thumbsdown';
export const RESERVED_TAGS: readonly string[] = [RESERVED_TAG_THUMBS_UP, RESERVED_TAG_THUMBS_DOWN];
export function isReservedTag(tag: string): boolean {
  return RESERVED_TAGS.includes(tag);
}

// System tags (mirror of Backend/shared/src/userTagConstants.ts) — friendly names
// admins apply via bulk-tag; the backend routes them to the shared `'system'` owner +
// records provenance. NORMALIZED values. NOT reserved. See docs/FeatureDesigns/goals-syllabus-revision-plan/student-placement.md.
export const SYSTEM_TAG_OWNER = 'system';
export const STUDENT_PLACEMENT_TAG = 'studentplacement';
export const STUDENT_PLACEMENT_TAG_LABEL = 'StudentPlacement';
export const FIRST_RUN_TAG = 'firstrun';
export const FIRST_RUN_TAG_LABEL = 'FirstRun';
export const DUEL_TAG = 'duel';
export const DUEL_TAG_LABEL = 'Duel';
export const SYSTEM_TAGS: readonly string[] = [STUDENT_PLACEMENT_TAG, FIRST_RUN_TAG, DUEL_TAG];
export const SYSTEM_TAG_LABELS: Readonly<Record<string, string>> = {
  [STUDENT_PLACEMENT_TAG]: STUDENT_PLACEMENT_TAG_LABEL,
  [FIRST_RUN_TAG]: FIRST_RUN_TAG_LABEL,
  [DUEL_TAG]: DUEL_TAG_LABEL,
};
export function isSystemTag(tag: string): boolean {
  return SYSTEM_TAGS.includes(tag);
}
/** Canonical display label for a system tag (pass the NORMALIZED form); falls back to the input. */
export function systemTagLabel(normalizedTag: string): string {
  return SYSTEM_TAG_LABELS[normalizedTag] ?? normalizedTag;
}

/** @deprecated Use NestedIds */
export type NestedQuestionIds = NestedIds;

export interface UserTagData {
  tag: string;
  questions: NestedIds;
  submissions: NestedIds;
  isDefault: boolean;
  /** True for shared system-pool tags surfaced to admins (e.g. studentplacement). */
  isSystem?: boolean;
}

export interface GetTagsResponse {
  tags: UserTagData[];
  defaultTags: string[];
}

/** Check if an item ID exists in a nested structure */
export function hasItemInTag(nested: NestedIds, itemId: string): boolean {
  for (const modules of Object.values(nested)) {
    for (const ids of Object.values(modules)) {
      if (ids.includes(itemId)) return true;
    }
  }
  return false;
}

/** @deprecated Use hasItemInTag */
export function hasQuestionInTag(questions: NestedIds, questionId: string): boolean {
  return hasItemInTag(questions, questionId);
}

/** Flatten any nested ID structure to a flat set */
export function flattenIds(nested: NestedIds): Set<string> {
  const ids = new Set<string>();
  for (const modules of Object.values(nested)) {
    for (const itemIds of Object.values(modules)) {
      for (const id of itemIds) ids.add(id);
    }
  }
  return ids;
}

/** @deprecated Use flattenIds */
export function flattenQuestionIds(questions: NestedQuestionIds): Set<string> {
  return flattenIds(questions);
}

/** Get question IDs for a specific subject + topModuleId */
export function getQuestionIdsForModule(questions: NestedQuestionIds, subject: string, topModuleId: string): string[] {
  return questions[subject]?.[topModuleId] ?? [];
}

/** Get question IDs for a specific subject (all modules) */
export function getQuestionIdsForSubject(questions: NestedQuestionIds, subject: string): string[] {
  const modules = questions[subject];
  if (!modules) return [];
  const ids: string[] = [];
  for (const qIds of Object.values(modules)) {
    ids.push(...qIds);
  }
  return ids;
}
